import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';
export function useQueuedChatFeedback(send, busy, options) {
    const queueRef = useRef([]);
    const sendRef = useRef(send);
    const busyRef = useRef(busy);
    const disabledRef = useRef(Boolean(options?.disabled));
    const drainingRef = useRef(false);
    const disabled = Boolean(options?.disabled);
    useLayoutEffect(()=>{
        sendRef.current = send;
    }, [
        send
    ]);
    useLayoutEffect(()=>{
        busyRef.current = busy;
        disabledRef.current = disabled;
    }, [
        busy,
        disabled
    ]);
    const markUndelivered = useCallback(()=>{
        drainingRef.current = false;
        // Keep head for retry on the next send / busy→idle edge; surface that
        // the line did not reach the agent so the stall is not silent.
        toast.error('Could not send feedback to Specra Assist. It will retry on the next action.');
    }, []);
    const drain = useCallback(()=>{
        if (drainingRef.current || busyRef.current || disabledRef.current) return;
        const next = queueRef.current[0];
        if (next === undefined) return;
        drainingRef.current = true;
        void Promise.resolve(sendRef.current(next)).then((started)=>{
            if (started === false) {
                markUndelivered();
                return;
            }
            // Drop only on confirmed start — even if busy flipped during the await
            // and the idle effect was cleaned up.
            if (queueRef.current[0] === next) {
                queueRef.current.shift();
            }
            drainingRef.current = false;
            // Continue FIFO while still idle (overlapping Accept before busy
            // flips). On failure we do not recurse — wait for a busy→idle edge.
            if (!busyRef.current && !disabledRef.current && queueRef.current.length > 0) {
                drain();
            }
        }).catch(()=>{
            markUndelivered();
        });
    }, [
        markUndelivered
    ]);
    useEffect(()=>{
        if (!busy && !disabled) {
            drain();
        }
    }, [
        busy,
        disabled,
        drain
    ]);
    return useCallback((message)=>{
        if (disabled) return;
        const trimmed = message.trim();
        if (!trimmed) return;
        queueRef.current.push(trimmed);
        if (!busy) drain();
    }, [
        busy,
        disabled,
        drain
    ]);
}
